import type { App } from 'vue'
import dayjs, { type Dayjs, type ConfigType } from 'dayjs'
import 'dayjs/locale/pt'

import pluginCustomParseFormat from 'dayjs/plugin/customParseFormat'
import pluginDuration from 'dayjs/plugin/duration'
import pluginIsBetween from 'dayjs/plugin/isBetween'
import pluginLocaleData from 'dayjs/plugin/localeData'
import pluginLocalizedFormat from 'dayjs/plugin/localizedFormat'
import pluginRelativeTime from 'dayjs/plugin/relativeTime'
import pluginTimezone from 'dayjs/plugin/timezone'
import pluginUtc from 'dayjs/plugin/utc'
import pluginWeekOfYear from 'dayjs/plugin/weekOfYear'

import i18n from '@/scripts/app-configs/i18n-config'
import { preferredLanguage } from '@/scripts/app-configs/language/languages-configs'
import pluginFormatDuration from './format-duration'

dayjs.locale(preferredLanguage)

dayjs.extend(pluginUtc)
dayjs.extend(pluginTimezone)
dayjs.extend(pluginLocalizedFormat)
dayjs.extend(pluginIsBetween)
dayjs.extend(pluginCustomParseFormat)
dayjs.extend(pluginRelativeTime)
dayjs.extend(pluginWeekOfYear)
dayjs.extend(pluginDuration)
dayjs.extend(pluginLocaleData)

//custom plugins
dayjs.extend(pluginFormatDuration)

const $date = dayjs

$date.prototype.divide = function divideDate() {
  return {
    day: this.date(),
    month: this.month(),
    year: this.year(),
    month_name: this.format('MMMM'),
  }
}

$date.prototype.shToday = function checkIfIsToday(d: ConfigType) {
  const date = dayjs(d)

  return dayjs().isSame(date, 'day')
}

$date.prototype.shYesterday = function shYesterday(d: ConfigType) {
  const date = dayjs(d)

  return dayjs().subtract(1, 'day').isSame(date, 'day')
}

$date.prototype.shDayDiff = function shDayDiff(d = new Date) {
  const date = dayjs(d).set('hour', 0).set('minute', 0).set('second', 0)
  const dateOrigin = dayjs(this.$d).set('hour', 0).set('minute', 0).set('second', 0)

  return dateOrigin.diff(date, 'day')
}

$date.prototype.dateDetails = function generateDateDetails(format?: string) {
  const date = dayjs(new Date(this * 1000))
  const dateMonth = date.get('month') + 1
  const dateYear = date.get('year')
  const dateTimestamp = date.unix()
  const dateUtcTimestamp = dateTimestamp + new Date(this).getTimezoneOffset() * 60
  const dateUtcDate = dayjs(new Date(dateUtcTimestamp * 1000))

  return {
    isToday: date.shToday(date),
    isYesterday: date.shYesterday(date),
    day: date.get('date'),
    month: dateMonth,
    year: dateYear,
    daysInMonth: new Date(dateYear, dateMonth, 0).getDate(),
    hour: date.get('hour'),
    minutes: date.get('minute'),
    seconds: date.get('second'),
    dateFormatted: date.format(format),
    timestamp: dateTimestamp,
    timezone: (new Date(this).getTimezoneOffset() * -1) / 60,
    utcTimestamp: dateUtcTimestamp,
    utcDate: dateUtcDate,
    utcFormatted: dateUtcDate.format(format),
  }
}

$date.prototype.defaultDiff = function defDiff(d: ConfigType, ignoreTime = false) {
  let date = dayjs(d)
  let refDate = this

  if (ignoreTime) {
    refDate = refDate.startOf('day')
    date = date.startOf('day')
  }

  const diff = refDate.diff(date, 'day')

  switch (diff) {
    case 0: {
      return i18n.t('LATEST_ACTIVITY.TODAY')
    }
    case 1: {
      return i18n.t('LATEST_ACTIVITY.YESTERDAY')
    }
    default: {
      return `${diff} ${i18n.t('COMMON.DAYS_AGO')}`
    }
  }
}

$date.prototype.howLongAgo = function howLongAgo(d: ConfigType) {
  const refDate = dayjs().utc()
  const date = dayjs.utc(d)

  const minutesDiff = refDate.diff(date, 'minute')

  if (minutesDiff < 1) {
    return i18n.t('COMMON.JUST_NOW')
  }

  if (minutesDiff < 60) {
    return i18n.t('COMMON.MINUTES_AGO_ABBREVIATED', { minutes: minutesDiff })
  }

  const isYesterday = refDate.subtract(1, 'day').isSame(date, 'day')

  if (isYesterday) {
    return i18n.t('LATEST_ACTIVITY.YESTERDAY')
  }

  const hoursDiff = refDate.diff(date, 'hour')

  if (hoursDiff < 24) {
    return i18n.t('COMMON.HOUR_AGO_ABBREVIATED', { hours: hoursDiff })
  }

  const daysDiff = refDate.startOf('day').diff(date.startOf('day'), 'day')

  if (daysDiff >= 2 && daysDiff <= 6) {
    return i18n.t('COMMON.DAYS_AGO_ABBREVIATED', { days: daysDiff })
  }

  if (daysDiff >= 7 && daysDiff <= 29) {
    const weeksDiff = Math.floor(daysDiff / 7)

    return i18n.t('COMMON.WEEK_AGO_ABBREVIATED', { weeks: weeksDiff })
  }

  const monthsDiff = refDate.diff(date, 'month')

  if (monthsDiff >= 1 && monthsDiff <= 11) {
    return i18n.t('COMMON.M_AGO_ABBREVIATED', { months: monthsDiff })
  }

  const yearsDiff = refDate.diff(date, 'year')

  return i18n.t('COMMON.Y_AGO_ABBREVIATED', { years: yearsDiff })
}

$date.prototype.defaultDiffAbbr = function defDiffAbrr(ignoreTime = false) {
  let date = this

  const dateTimezone = this.format('Z') !== this.utc().format('Z') ? this.$x?.$timezone : ''
  let refDate = dateTimezone ? dayjs().tz(dateTimezone) : dayjs()

  if (ignoreTime) {
    refDate = refDate.startOf('day')
    date = date.startOf('day')
  }

  const diff = refDate.diff(date, 'day')

  switch (diff) {
    case 0: {
      return i18n.t('LATEST_ACTIVITY.TODAY')
    }
    default: {
      return i18n.t('COMMON.DAYS_AGO_ABBREVIATED', { days: diff })
    }
  }
}

$date.prototype.verboseDate = function formatVerbose(format?: string) {
  const today = i18n.t('LATEST_ACTIVITY.TODAY')
  const yesterday = i18n.t('LATEST_ACTIVITY.YESTERDAY')
  const pastDate = this.format(format)
  let verboseDate = pastDate

  if (this.shToday(this)) {
    verboseDate = today
  }

  if (this.shYesterday(this)) {
    verboseDate = yesterday
  }

  const hour = `${String(this.get('hour')).padStart(2, '0')}:${String(this.get('minute')).padStart(2, '0')}h`

  return `${verboseDate}, ${hour}`
}

$date.prototype.age = function () {
  return dayjs().diff(this, 'year')
}

export { $date, Dayjs }

export default {
  install(app: App) {
    app.config.globalProperties.$date = $date
  },
}
